// View state singleton with pub/sub pattern
// Manages zoom, pan, and viewport state with localStorage persistence

/**
 * @typedef {object} ViewStateData
 * @property {number} zoom
 * @property {number} panX
 * @property {number} panY
 */

class ViewStateManager {
  /**
   * @param {object} [options]
   * @param {string} [options.storagePrefix='player'] - Prefix for localStorage keys
   * @param {number} [options.defaultZoom=1.0]
   * @param {number} [options.defaultPanX=0]
   * @param {number} [options.defaultPanY=0]
   * @param {number} [options.minZoom=0.25]
   * @param {number} [options.maxZoom=4.0]
   */
  constructor(options = {}) {
    const {
      storagePrefix = 'player',
      defaultZoom = 1.0,
      defaultPanX = 0,
      defaultPanY = 0,
      minZoom = 0.25,
      maxZoom = 4.0,
    } = options;

    /** @type {Set<() => void>} */
    this.listeners = new Set();

    // Options
    /** @type {string} */
    this.storagePrefix = storagePrefix;
    /** @type {number} */
    this.defaultZoom = defaultZoom;
    /** @type {number} */
    this.defaultPanX = defaultPanX;
    /** @type {number} */
    this.defaultPanY = defaultPanY;
    /** @type {number} */
    this.minZoom = minZoom;
    /** @type {number} */
    this.maxZoom = maxZoom;

    // State
    /** @type {number} */
    this.zoom = defaultZoom;
    /** @type {number} */
    this.panX = defaultPanX;
    /** @type {number} */
    this.panY = defaultPanY;
    /** @type {string|null} */
    this.storageId = null;

    // Debounce timer for saving
    /** @type {ReturnType<typeof setTimeout>|null} */
    this.saveTimer = null;
  }

  /**
   * Subscribe to state changes
   * @param {() => void} fn - Callback function
   * @returns {() => void} Unsubscribe function
   */
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Notify all listeners of state change
   */
  notify() {
    this.listeners.forEach((fn) => {
      fn();
    });
  }

  /**
   * Get localStorage key for view state
   * @returns {string|null}
   */
  getStorageKey() {
    if (!this.storageId) return null;
    return `rpgish-${this.storagePrefix}-view-state-${this.storageId}`;
  }

  /**
   * Set storage ID and load saved state
   * @param {string|null} id
   */
  setStorageId(id) {
    this.storageId = id;
    if (id) {
      this.loadViewState();
    } else {
      this.resetToDefaults();
    }
  }

  /**
   * Load view state from localStorage
   */
  loadViewState() {
    const key = this.getStorageKey();
    if (!key) return;

    try {
      const savedState = localStorage.getItem(key);
      if (savedState) {
        /** @type {ViewStateData} */
        const state = JSON.parse(savedState);
        this.zoom = state.zoom ?? this.defaultZoom;
        this.panX = state.panX ?? this.defaultPanX;
        this.panY = state.panY ?? this.defaultPanY;
        this.notify();
      }
    } catch (error) {
      console.error('Failed to load view state:', error);
    }
  }

  /**
   * Save view state to localStorage (debounced)
   */
  saveViewState() {
    const key = this.getStorageKey();
    if (!key) return;

    // Clear existing timer
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    // Debounce save by 100ms
    this.saveTimer = setTimeout(() => {
      try {
        /** @type {ViewStateData} */
        const state = {
          zoom: this.zoom,
          panX: this.panX,
          panY: this.panY,
        };
        localStorage.setItem(key, JSON.stringify(state));
      } catch (error) {
        console.error('Failed to save view state:', error);
      }
    }, 100);
  }

  /**
   * Clear view state from localStorage
   */
  clearViewState() {
    const key = this.getStorageKey();
    if (!key) return;

    try {
      localStorage.removeItem(key);
      this.resetToDefaults();
    } catch (error) {
      console.error('Failed to clear view state:', error);
    }
  }

  /**
   * Reset to default values
   */
  resetToDefaults() {
    this.zoom = this.defaultZoom;
    this.panX = this.defaultPanX;
    this.panY = this.defaultPanY;
    this.notify();
  }

  /**
   * Set zoom level
   * @param {number} zoom
   */
  setZoom(zoom) {
    this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
    this.saveViewState();
    this.notify();
  }

  /**
   * Adjust zoom by delta
   * @param {number} delta - Positive to zoom in, negative to zoom out
   */
  adjustZoom(delta) {
    this.setZoom(this.zoom + delta);
  }

  /**
   * Zoom in by a step
   * @param {number} [step=0.25]
   */
  zoomIn(step = 0.25) {
    this.adjustZoom(step);
  }

  /**
   * Zoom out by a step
   * @param {number} [step=0.25]
   */
  zoomOut(step = 0.25) {
    this.adjustZoom(-step);
  }

  /**
   * Set pan position
   * @param {number} panX
   * @param {number} panY
   */
  setPan(panX, panY) {
    this.panX = panX;
    this.panY = panY;
    this.saveViewState();
    this.notify();
  }

  /**
   * Adjust pan by delta
   * @param {number} deltaX
   * @param {number} deltaY
   */
  adjustPan(deltaX, deltaY) {
    this.setPan(this.panX + deltaX, this.panY + deltaY);
  }

  /**
   * Center view on a specific grid cell
   * @param {number} col
   * @param {number} row
   * @param {number} gridSize
   * @param {number} canvasWidth
   * @param {number} canvasHeight
   */
  centerOnCell(col, row, gridSize, canvasWidth, canvasHeight) {
    const cellCenterX = (col + 0.5) * gridSize;
    const cellCenterY = (row + 0.5) * gridSize;

    this.panX = canvasWidth / 2 / this.zoom - cellCenterX;
    this.panY = canvasHeight / 2 / this.zoom - cellCenterY;

    this.saveViewState();
    this.notify();
  }

  /**
   * Get current view state
   * @returns {ViewStateData}
   */
  getState() {
    return {
      zoom: this.zoom,
      panX: this.panX,
      panY: this.panY,
    };
  }
}

// Singleton instance for player view
export const viewState = new ViewStateManager({
  storagePrefix: 'player',
  defaultZoom: 1.0,
});

// Singleton instance for editor view (can have different defaults)
export const editorViewState = new ViewStateManager({
  storagePrefix: 'editor',
  defaultZoom: 0.5,
  minZoom: 0.1,
  maxZoom: 2.0,
});
